import React, { FunctionComponent, useEffect, useMemo, useState } from 'react';
import '../productPage/producthome.css';
import { Box } from '@mui/material';
import * as styles from '../productPage/tableComponents/ProductPage_style';
import { useLocation, useNavigate } from 'react-router-dom';
import BreadCrumbs from '../common/BreadCrumbs';
import AutomotiveOverviewPage from './AutomotiveOverviewPage';
import TechnicalResources from './TechnicalResources';
import ToolPage from '../resourcesPage/simulationPage/ToolPage';
import TabsComponent from '../common/TabsComponent';
import { getTabTypography } from '../investorPage/investorRelationHome';
import { useResponsive } from '../../contexts/ResponsiveContext';
import ReactiveBanner from '../common/ReactiveBanner';
import { ApplicationsAutomotiveContent } from '../../graphql/pimcore/modules/siteContent/types/ApplicationsContent';
import { useSalesAndResourcesSimulationToolsContent } from '../../graphql/pimcore/modules/siteContent/hooks/useSalesAndResourcesSimulationToolsContent';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function CustomTabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;

    return (
        <div
            role="automotive-panel"
            hidden={value !== index}
            id={`automotive-panel-${index}`}
            aria-labelledby={`automotive-panel-tab-${index}`}
            {...other}
        >
            {value === index && <Box>{children}</Box>}
        </div>
    );
}

interface AutomotiveHomeProps {
    cmsContent: ApplicationsAutomotiveContent;
}

const AutomotiveHome: FunctionComponent<AutomotiveHomeProps> = ({ cmsContent }) => {
    const [value, setValue] = useState(0);
    const location = useLocation();
    const navigate = useNavigate();
    const [openMenu, setOpenMenu] = React.useState(false);
    const { contentTree: toolsContent } = useSalesAndResourcesSimulationToolsContent();
    const pathMap = useMemo(
        () => [
            '/Applications/automotive/',
            '/Applications/automotive/technical-resources',
            '/Applications/automotive/simulation-tools',
        ],
        []
    );

    useEffect(() => {
        const newValue = pathMap.indexOf(location.pathname);
        setValue(newValue >= 0 ? newValue : 0);
    }, [location.pathname, pathMap]);

    const handleChange = (event: React.SyntheticEvent | null, newValue: number) => {
        setValue(newValue);
        const path = pathMap[newValue] ?? pathMap[0];
        if (path) {
            navigate(path);
        }
    };

    const { responsiveWidth } = useResponsive();
    return (
        <div key={location.pathname} style={styles.productPageContainer}>
            <div style={styles.breadCrumbs}>
                <Box
                    sx={{
                        width: responsiveWidth,
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'flex-start',
                        justifyContent: 'center',
                        margin: 'auto',
                    }}
                >
                    <BreadCrumbs navigationPath="/Home/Applications/Automotive" />
                </Box>
            </div>
            <ReactiveBanner
                title={cmsContent.pageTitle.content}
                description={cmsContent.pageSubtitle.content}
                bannerImage={cmsContent.pageTitleBackgroundImage.imageUrl}
            />

            <Box sx={{ width: '100%', backgroundColor: '#7A7C81' }} data-testid={'outter_box'}>
                <TabsComponent
                    isMenuExpandMoreIcon
                    url={value}
                    setOpenMenu={setOpenMenu}
                    openMenu={openMenu}
                    handleChange={handleChange}
                    menuItems={[
                        { label: cmsContent.tabs.Overview.title.content, value: 0 },
                        { label: cmsContent.tabs.Resources.title.content, value: 1 },
                        { label: cmsContent.tabs.SimulationTools.title.content, value: 2 },
                    ]}
                    tabTypography={{
                        ...getTabTypography,
                    }}
                />
                <CustomTabPanel value={value} index={0}>
                    <AutomotiveOverviewPage cmsContent={cmsContent} />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={1}>
                    <TechnicalResources backgroundColor={'white'} />
                </CustomTabPanel>
                <CustomTabPanel value={value} index={2}>
                    <ToolPage cmsContent={toolsContent.Overview} />
                </CustomTabPanel>
            </Box>
        </div>
    );
};

export default AutomotiveHome;
